// File: frontend/src/components/marketing/visuals/NetworkTopologyVisual.js
//
// SVG network diagram: the PesaFii hub, hotspot locations, the connections
// between them, and small data packets travelling to the hub. Pure SVG, no
// canvas, so it stays sharp at any size and follows the theme.
//
// It is a topology diagram, NOT a geographic map — the positions are a fixed
// ring around the hub (see POSITIONS), so nothing here should be described as
// showing where anything is.
//
// ── SELECTING A LOCATION ────────────────────────────────────────────────────
//
// With `onSelect`, every location becomes a real control: clickable, tappable
// with a finger-sized hit area, reachable by Tab, operated with Enter or Space,
// and announced with its own state. The section beside it shows the selected
// location's details, so the two always agree. Without `onSelect` the diagram is
// a picture with a single highlighted node and one `role="img"` label — which
// is what the hero backdrop and any static rendering need.
//
// The static markup is the finished diagram (lines drawn, nodes shown), so the
// prerendered page and reduced-motion visitors see it complete.
//
// Motion (GSAP, full diagram only, not the faint hero backdrop):
//   · on entry: the hub appears, the connections draw, the locations light up
//     one by one, then the selected location starts a slow halo;
//   · while on screen: packets travel (SVG <animateMotion>) and the halo runs;
//     both stop when the diagram scrolls away or the tab is hidden.
// `compact` keeps the three strongest nodes for small screens.

import React, { useId, useMemo, useRef } from "react";
import { Box, useTheme, alpha } from "@mui/material";
import { DEMO_LOCATIONS, DEMO_DEFAULT_LOCATION_ID } from "../../../data/marketingDemoData";
import { useOnScreen } from "../motion";
import { gsap, useGSAP, MQ, useMediaMatch, isBelowFold, playOnEnter, pauseWhenHidden } from "../landingMotion";

const VB_W = 900, VB_H = 520;
const HUB = { x: 450, y: 262 };

// Fixed, balanced positions for up to 5 nodes around the hub.
const POSITIONS = [
  { x: 150, y: 120 },
  { x: 760, y: 150 },
  { x: 120, y: 400 },
  { x: 790, y: 388 },
  { x: 450, y: 64 },
];

// Quadratic curve from a node to the hub, bowed toward the vertical centre.
function curve(n) {
  const mx = (n.x + HUB.x) / 2;
  const my = (n.y + HUB.y) / 2 + (n.y < HUB.y ? -34 : 34);
  return `M ${n.x} ${n.y} Q ${mx} ${my} ${HUB.x} ${HUB.y}`;
}

export default function NetworkTopologyVisual({
  compact = false,
  showPackets = true,
  height = 420,
  faint = false,          // ambient hero backdrop: lower contrast, no labels, no entry
  showLabels = true,
  selectedId = DEMO_DEFAULT_LOCATION_ID,
  onSelect,               // when given, the locations become real controls
  ariaLabel,
}) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const green = theme.palette.primary.main;
  const muted = theme.palette.text.disabled;
  const reduce = useMediaMatch(MQ.reduced);
  const root = useRef(null);
  const onScreen = useOnScreen(root);
  const gid = useId().replace(/:/g, "");
  const interactive = typeof onSelect === "function" && !faint;

  const line = alpha(green, faint ? (dark ? 0.22 : 0.18) : (dark ? 0.6 : 0.5));
  const ink = theme.palette.text.primary;
  // The compact diagram is drawn at about a third of its size on a phone, so its
  // nodes and labels are scaled up to stay readable.
  const k = compact ? 1.9 : 1;
  const halo = theme.palette.background.default;

  const nodes = useMemo(() => {
    const list = DEMO_LOCATIONS.slice(0, compact ? 3 : 5);
    return list.map((n, i) => ({ ...n, ...POSITIONS[i], path: curve(POSITIONS[i]) }));
  }, [compact]);

  const online = nodes.filter((n) => n.online).length;
  const label = ariaLabel || `Sample diagram of ${nodes.length} hotspot locations connected to PesaFii, ${online} online`;
  const packets = showPackets && !reduce && onScreen;
  const symbol = dark ? "/pesafii-symbol-white-green.svg" : "/pesafii-symbol-black-green.svg";

  useGSAP(
    () => {
      if (faint) return undefined;
      const mm = gsap.matchMedia();
      mm.add({ reduced: MQ.reduced, always: MQ.always }, (ctx) => {
        if (ctx.conditions.reduced) return undefined;
        const el = root.current;
        const q = gsap.utils.selector(el);
        const selected = q('[data-halo="selected"]');
        const pulse = gsap.fromTo(selected, { scale: 1, opacity: 0.55 }, {
          scale: 1.9, opacity: 0, duration: 2.4, ease: "power1.out", repeat: -1, paused: true, transformOrigin: "50% 50%",
        });
        const rings = gsap.to(q("[data-ring]"), {
          opacity: 0.12, duration: 2.8, ease: "sine.inOut", yoyo: true, repeat: -1, stagger: 0.6, paused: true,
        });
        const ambient = gsap.timeline({ paused: true }).add(pulse, 0).add(rings, 0);

        // Build the entry only for a diagram the reader has not reached yet.
        if (!isBelowFold(el)) return pauseWhenHidden(el, ambient);

        gsap.set(q("[data-link]"), { strokeDasharray: 1, strokeDashoffset: 1 });
        gsap.set(q("[data-node]"), { opacity: 0, scale: 0.6, transformOrigin: "50% 50%" });
        gsap.set(q("[data-hub]"), { opacity: 0, scale: 0.85, transformOrigin: "50% 50%" });
        let stopAmbient = () => {};
        playOnEnter(el, () => {
          gsap.timeline({ defaults: { ease: "power3.out" } })
            .to(q("[data-hub]"), { opacity: 1, scale: 1, duration: 0.5 })
            // clearProps hands the dash pattern back to the markup (offline links are dashed).
            .to(q("[data-link]"), { strokeDashoffset: 0, duration: 0.9, stagger: 0.1, ease: "power2.inOut", clearProps: "strokeDasharray,strokeDashoffset" }, "-=0.2")
            .to(q("[data-node]"), { opacity: 1, scale: 1, duration: 0.45, stagger: 0.12, ease: "back.out(1.6)" }, "-=0.6")
            .add(() => { stopAmbient = pauseWhenHidden(el, ambient); });
        });
        return () => stopAmbient();
      });
      return undefined;
    },
    // The selected halo is attached to whichever node carries it, so the context
    // is rebuilt when the selection moves.
    { scope: root, dependencies: [faint, compact, selectedId] }
  );

  const keyActivate = (n) => (e) => {
    if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
    e.preventDefault();
    onSelect(n.id);
  };

  return (
    <Box
      ref={root}
      aria-hidden={faint || undefined}
      // A group of controls is not an image. Only the non-interactive diagram
      // describes itself as one.
      role={faint ? undefined : interactive ? "group" : "img"}
      aria-label={faint ? undefined : label}
      sx={{
        width: "100%", lineHeight: 0,
        // A browser's default focus ring on an SVG <g> is drawn around the
        // group's bounding box — which here includes the 46-unit hit circle and
        // both labels — and several browsers do not draw it at all. So the ring
        // is a real element in the drawing, shown only for keyboard focus.
        "& g[role='button']": { outline: "none" },
        "& g[role='button']:focus-visible .pf-node-focus": { opacity: 1 },
        "& g[role='button']:hover .pf-node-hover": { opacity: 1 },
      }}
    >
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" height={height} preserveAspectRatio="xMidYMid meet"
        style={{ maxHeight: height, overflow: "visible" }}>
        <defs>
          <radialGradient id={`hub-glow-${gid}`}>
            <stop offset="0%" stopColor={green} stopOpacity={faint ? 0.18 : dark ? 0.42 : 0.3} />
            <stop offset="100%" stopColor={green} stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* hub glow and signal rings */}
        <circle cx={HUB.x} cy={HUB.y} r={150} fill={`url(#hub-glow-${gid})`} />
        {[80, 130, 182].map((r) => (
          <circle key={r} data-ring="" cx={HUB.x} cy={HUB.y} r={r} fill="none"
            stroke={alpha(green, faint ? 0.1 : 0.22)} strokeWidth="1" opacity="0.5" />
        ))}

        {/* connections */}
        {nodes.map((n) => (
          <path key={`p-${n.id}`} data-link="" d={n.path} pathLength="1" fill="none"
            stroke={n.online ? line : alpha(muted, faint ? 0.2 : 0.5)}
            strokeWidth={faint ? 1.2 : 2}
            strokeDasharray={n.online ? undefined : "0.02 0.018"}
            strokeLinecap="round" />
        ))}

        {/* data packets travelling node -> hub, only while on screen */}
        {packets && nodes.filter((n) => n.online).map((n, i) => (
          <circle key={`pkt-${n.id}`} r={faint ? 3 : 4} fill={green}>
            <animateMotion dur={`${3.4 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.6}s`} path={n.path} keyPoints="0;1" keyTimes="0;1" calcMode="linear" />
            <animate attributeName="opacity" values="0;1;1;0" dur={`${3.4 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.6}s`} />
          </circle>
        ))}

        {/* locations */}
        {nodes.map((n) => {
          const isSelected = !faint && n.id === selectedId;
          const stroke = n.online ? green : muted;
          return (
            <g
              key={`n-${n.id}`}
              data-node=""
              {...(interactive && {
                role: "button",
                tabIndex: 0,
                "aria-pressed": isSelected,
                "aria-label": `${n.name}, ${n.online ? `online, ${n.users} connected` : "offline"}`,
                onClick: () => onSelect(n.id),
                onKeyDown: keyActivate(n),
                style: { cursor: "pointer" },
              })}
            >
              {/* Finger-sized hit area. Invisible, and only where it is real:
                  a transparent target on a picture would be a lie. */}
              {interactive && <circle cx={n.x} cy={n.y} r={46} fill="transparent" />}
              {interactive && (
                <>
                  <circle className="pf-node-hover" cx={n.x} cy={n.y} r={(isSelected ? 18 : 15) * k + 7}
                    fill="none" stroke={alpha(green, 0.45)} strokeWidth="2" opacity="0"
                    style={{ transition: "opacity 0.18s ease" }} />
                  <circle className="pf-node-focus" cx={n.x} cy={n.y} r={(isSelected ? 18 : 15) * k + 12}
                    fill="none" stroke={green} strokeWidth="3" strokeDasharray="5 4" opacity="0" />
                </>
              )}
              {isSelected && (
                <circle data-halo="selected" cx={n.x} cy={n.y} r={20 * k} fill="none" stroke={green} strokeWidth="2" opacity="0" />
              )}
              <circle cx={n.x} cy={n.y} r={(isSelected ? 18 : 15) * k}
                fill={theme.palette.background.paper}
                stroke={stroke} strokeWidth={isSelected ? 3.5 : 2.5}
                strokeDasharray={n.online ? undefined : "4 3"} />
              <circle cx={n.x} cy={n.y} r={(isSelected ? 7 : 5.5) * k} fill={n.online ? green : alpha(muted, 0.8)} />
              {!faint && showLabels && (
                <g>
                  {/* The diagram is drawn at about three-quarters of its viewBox
                      width on a desktop column, so these sizes are larger than
                      they look here — 21 lands at roughly 16px on screen. */}
                  <text x={n.x} y={n.y + 44 * k} textAnchor="middle" fontSize={21 * k}
                    fontWeight={isSelected ? 800 : 700}
                    fill={ink} stroke={halo} strokeWidth={5.5 * k} paintOrder="stroke" fontFamily="Inter, sans-serif">
                    {n.name}
                  </text>
                  <text x={n.x} y={n.y + 66 * k} textAnchor="middle" fontSize={16 * k} fontWeight="600"
                    fill={n.online ? theme.palette.success.main : muted} stroke={halo} strokeWidth={4.5 * k} paintOrder="stroke" fontFamily="Inter, sans-serif">
                    {n.online ? `Online · ${n.users} users` : "Offline"}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* central hub */}
        <g data-hub="">
          <circle cx={HUB.x} cy={HUB.y} r={46} fill={theme.palette.background.paper} stroke={alpha(green, faint ? 0.35 : 0.75)} strokeWidth="2.5" />
          <image href={symbol} x={HUB.x - 27} y={HUB.y - 27} width="54" height="54" preserveAspectRatio="xMidYMid meet" />
        </g>
      </svg>
    </Box>
  );
}
